import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from "@material-ui/core";
import { Helmet } from "react-helmet";
import PhoneIcon from "@material-ui/icons/Phone";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import MenuIcon from "@material-ui/icons/Menu";
import CloseIcon from "@material-ui/icons/Close";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import { Instance } from "./Config";

/* ─── Design Tokens ─── */
const T = {
  ink: "#1a1a2e",
  inkSoft: "#2a2a3e",
  paper: "#f6f5f1",
  paperWhite: "#fdfcfa",
  ruled: "#e8e6df",
  leaf: "#2d5016",
  leafLight: "rgba(45,80,22,0.07)",
  leafBorder: "rgba(45,80,22,0.18)",
  kraft: "#8b7355",
  pencil: "#6b7280",
  pencilLight: "#9ca3af",
  white: "#ffffff",
  red: "#c44545",
  display: "'Instrument Serif', Georgia, 'Times New Roman', serif",
  body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  radius: "8px",
};

/* ─── Reusable mini-card style ─── */
const cardShadow = "0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.03)";

/* ─── Interactive: Live Order Feed ─── */
const orderFeed = [
  { name: "Mehta Traders", items: 24, cats: 6, total: "18,400" },
  { name: "Sharma & Sons", items: 12, cats: 3, total: "7,250" },
  { name: "Patel Industries", items: 8, cats: 2, total: "4,120" },
  { name: "Gupta Textiles", items: 36, cats: 8, total: "31,200" },
  { name: "Rajesh Enterprises", items: 15, cats: 4, total: "11,800" },
  { name: "Agarwal & Co.", items: 20, cats: 5, total: "22,600" },
];

/* ─── Interactive: FAQ Accordion ─── */
const FaqItem = ({ q, a }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: "100%", padding: "20px 0", background: "none", border: "none",
          cursor: "pointer", display: "flex", justifyContent: "space-between",
          alignItems: "center", textAlign: "left", fontFamily: T.body,
        }}
      >
        <span style={{ fontSize: "15px", fontWeight: 700, color: T.ink }}>{q}</span>
        <span style={{
          fontSize: "20px", color: T.pencilLight, transition: "transform 0.3s ease",
          transform: isOpen ? "rotate(45deg)" : "rotate(0deg)", flexShrink: 0, marginLeft: "16px",
          lineHeight: 1,
        }}>+</span>
      </button>
      <div style={{
        maxHeight: isOpen ? "200px" : "0", overflow: "hidden",
        transition: "max-height 0.35s ease, opacity 0.3s ease",
        opacity: isOpen ? 1 : 0,
      }}>
        <p style={{
          fontSize: "14px", color: T.pencil, lineHeight: 1.65, margin: "0 0 20px",
          fontFamily: T.body, paddingRight: "40px",
        }}>{a}</p>
      </div>
      <hr style={{ border: "none", borderTop: `1px solid ${T.ruled}`, margin: 0 }} />
    </div>
  );
};

/* ─── Interactive: Animated Counter ─── */
const AnimatedCount = ({ target, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAnimated.current) {
        hasAnimated.current = true;
        const duration = 1200;
        const start = performance.now();
        const step = (now) => {
          const elapsed = now - start;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.round(eased * target));
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref}>{count}{suffix}</span>;
};

/* ─── Interactive: Copy Link Demo ─── */
const StoreLinkDemo = () => {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{
      background: T.white, border: `1px solid ${T.ruled}`, borderRadius: "8px",
      padding: "12px 14px", marginTop: "16px",
    }}>
      <p style={{ fontSize: "11px", color: T.pencilLight, margin: "0 0 6px", fontFamily: T.body }}>Your store link</p>
      <div style={{
        background: T.paper, borderRadius: "4px", padding: "8px 10px",
        fontSize: "12px", color: T.ink, fontFamily: T.body, fontWeight: 500,
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <span>ecom.skyhubs.in/your-store</span>
        <button
          onClick={() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }}
          style={{
            fontSize: "10px", fontWeight: 700, fontFamily: T.body,
            color: copied ? T.leaf : T.ink, background: copied ? T.leafLight : T.ruled,
            border: "none", borderRadius: "3px", padding: "3px 8px",
            cursor: "pointer", transition: "all 0.2s",
          }}
        >
          {copied ? "✓ Copied!" : "Copy"}
        </button>
      </div>
    </div>
  );
};

const MainScreen = () => {
  const [open, setOpen] = useState(false);
  const [mobileNoValid, setMobileNoValid] = useState(true);
  const [mobileNo, setMobileNo] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactStatus, setContactStatus] = useState(null);
  const [liveOrderIdx, setLiveOrderIdx] = useState(0);
  const [demoCart, setDemoCart] = useState({});
  const [showTopBtn, setShowTopBtn] = useState(false);
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncStep, setSyncStep] = useState(0);
  const history = useHistory();

  useEffect(() => {
    document.body.style.margin = "0";
    document.body.style.paddingBottom = "0";

    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      setShowTopBtn(window.scrollY > 600);
    };
    window.addEventListener("scroll", handleScroll);

    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) e.target.classList.add("h-visible");
      }),
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" },
    );
    document.querySelectorAll(".h-reveal").forEach((el) => observer.observe(el));

    return () => { window.removeEventListener("scroll", handleScroll); observer.disconnect(); };
  }, []);

  // Live order feed cycling
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveOrderIdx((prev) => (prev + 1) % orderFeed.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mobileNo.length === 10) {
      setIsLoading(true);
      Instance.get(`/saral/about?mobileNo=${mobileNo}`)
        .then((res) => {
          if (res.data.result.length !== 0) history.push(`/${res.data.result[0].MOBNO}/login`);
          else { setIsLoading(false); setMobileNoValid(false); }
        })
        .catch(() => { setIsLoading(false); setMobileNoValid(false); });
    } else setMobileNoValid(false);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) return;
    setContactLoading(true);
    setContactStatus(null);
    try {
      await Instance.post("/contact", contactForm);
      setContactStatus("success");
      setContactForm({ name: "", email: "", phone: "", message: "" });
    } catch { setContactStatus("error"); }
    finally { setContactLoading(false); }
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 72, behavior: "smooth" });
  };

  return (
    <div style={{ background: T.paper, fontFamily: T.body, color: T.ink, overflow: "hidden" }}>
      <Helmet>
        <title>Hub360 — Online ordering for wholesale businesses</title>
        <meta name="description" content="Hub360 helps wholesale distributors take orders online. Your products, your prices, your customers — with Saral Accounting integration built in." />
        <meta name="keywords" content="B2B ecommerce, wholesale platform, Saral Accounting integration, order management, wholesale ordering India, Hub360" />
        <link rel="canonical" href="https://ecom.skyhubs.in" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Hub360 — Online ordering for wholesale businesses" />
        <meta property="og:description" content="Take wholesale orders online with Saral Accounting integration." />
        <meta property="og:url" content="https://ecom.skyhubs.in" />
        <meta property="og:site_name" content="Hub360" />
        <meta property="og:image" content="https://ecom.skyhubs.in/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Hub360 — Online ordering for wholesale businesses" />
        <meta name="twitter:description" content="Take wholesale orders online with Saral Accounting integration." />
        <meta name="twitter:image" content="https://ecom.skyhubs.in/og-image.png" />
        <script type="application/ld+json">{`{
          "@context":"https://schema.org","@type":"SoftwareApplication","name":"Hub360",
          "applicationCategory":"BusinessApplication","operatingSystem":"Web, Android",
          "description":"Online ordering platform for wholesale businesses with Saral Accounting integration",
          "url":"https://ecom.skyhubs.in",
          "author":{"@type":"Organization","name":"Skyhubs Technolabs","url":"https://skyhubs.in"},
          "offers":{"@type":"Offer","price":"0","priceCurrency":"INR","description":"Free trial available"}
        }`}</script>
        <script type="application/ld+json">{`{
          "@context":"https://schema.org","@type":"Organization","name":"Hub360",
          "url":"https://ecom.skyhubs.in","logo":"https://ecom.skyhubs.in/favicon.png",
          "contactPoint":{"@type":"ContactPoint","telephone":"+91-99045-54232","contactType":"customer service","email":"info@skyhubs.in","availableLanguage":["English","Hindi","Gujarati"]}
        }`}</script>
      </Helmet>

      <ShopModal handleClose={handleClose} handleSubmit={handleSubmit} isLoading={isLoading}
        mobileNo={mobileNo} mobileNoValid={mobileNoValid} setMobileNo={setMobileNo} open={open} />

      <style>{`
        *, *::before, *::after { box-sizing: border-box; }

        .h-reveal { opacity: 0; transform: translateY(16px); transition: opacity 0.5s ease, transform 0.5s ease; }
        .h-reveal.h-visible { opacity: 1; transform: translateY(0); }

        .h-nav-link {
          background: none; border: none; cursor: pointer; font-size: 14px;
          font-weight: 500; color: ${T.pencil}; padding: 6px 0;
          transition: color 0.2s; font-family: ${T.body};
        }
        .h-nav-link:hover { color: ${T.ink}; }

        .h-input {
          width: 100%; padding: 11px 14px; font-size: 14px;
          border: 1.5px solid ${T.ruled}; border-radius: 6px;
          outline: none; transition: border-color 0.2s, box-shadow 0.2s;
          background: ${T.white}; font-family: ${T.body}; color: ${T.ink};
        }
        .h-input:focus { border-color: ${T.leaf}; box-shadow: 0 0 0 3px ${T.leafLight}; }
        .h-input::placeholder { color: ${T.pencilLight}; }

        .h-ruled { border: none; border-top: 1px solid ${T.ruled}; margin: 0; }

        .h-feature-row {
          display: grid; grid-template-columns: 200px 1fr; gap: 24px;
          padding: 28px 0; align-items: baseline;
        }

        .order-card {
          animation: orderSlide 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.6s both;
        }
        @keyframes orderSlide {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .h-card-hover { transition: transform 0.25s ease, box-shadow 0.25s ease; }
        .h-card-hover:hover { transform: translateY(-3px); box-shadow: 0 4px 16px rgba(0,0,0,0.06); }

        @keyframes orderFadeIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .order-live-card { animation: orderFadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) both; }

        @keyframes pulse-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .pulse-dot { animation: pulse-dot 2s ease-in-out infinite; }

        .demo-add-btn {
          background: ${T.leafLight}; border: 1px solid ${T.leafBorder}; color: ${T.leaf};
          font-size: 12px; font-weight: 700; padding: 4px 10px; border-radius: 4px;
          cursor: pointer; font-family: ${T.body}; transition: all 0.2s;
        }
        .demo-add-btn:hover { background: ${T.leaf}; color: ${T.white}; border-color: ${T.leaf}; }

        .demo-qty-btn {
          width: 22px; height: 22px; border-radius: 4px; border: 1px solid ${T.ruled};
          background: ${T.white}; cursor: pointer; font-size: 14px; color: ${T.ink};
          display: flex; align-items: center; justify-content: center; font-family: ${T.body};
          transition: all 0.15s;
        }
        .demo-qty-btn:hover { background: ${T.paper}; }

        .back-to-top {
          position: fixed; bottom: 28px; right: 28px; z-index: 999;
          width: 40px; height: 40px; border-radius: 50%; border: 1px solid ${T.ruled};
          background: ${T.white}; color: ${T.ink}; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 2px 10px rgba(0,0,0,0.08);
          transition: opacity 0.3s, transform 0.3s;
          font-size: 18px; font-family: ${T.body};
        }
        .back-to-top:hover { background: ${T.ink}; color: ${T.paperWhite}; border-color: ${T.ink}; }

        @keyframes syncPulse {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        .sync-bar {
          height: 3px; border-radius: 2px; overflow: hidden;
          background: linear-gradient(90deg, ${T.ruled}, ${T.leaf}, ${T.ruled});
          background-size: 200% 100%;
          animation: syncPulse 1.5s ease-in-out infinite;
        }

        .desktop-nav { display: flex; gap: 24px; align-items: center; }
        .mobile-nav-toggle {
          display: none; background: none; border: none;
          cursor: pointer; padding: 4px; color: ${T.ink};
        }
        .mobile-overlay {
          display: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(253,252,250,0.97); z-index: 9999;
          flex-direction: column; align-items: center; justify-content: center; gap: 4px;
          backdrop-filter: blur(12px);
        }
        .mobile-overlay.open { display: flex; }
        .mobile-overlay-close {
          position: absolute; top: 20px; right: 20px;
          background: none; border: none; cursor: pointer; color: ${T.ink}; padding: 4px;
        }
        .mobile-overlay button.mob-item {
          background: none; border: none; cursor: pointer;
          font-size: 20px; font-weight: 600; color: ${T.pencil};
          padding: 12px 0; font-family: ${T.body}; transition: color 0.2s;
        }
        .mobile-overlay button.mob-item:hover { color: ${T.ink}; }

        @media (max-width: 900px) {
          .desktop-nav { display: none !important; }
          .mobile-nav-toggle { display: flex !important; }
          .h-feature-row { grid-template-columns: 1fr !important; gap: 8px !important; }
          .hero-grid { grid-template-columns: 1fr !important; }
          .integration-grid { grid-template-columns: 1fr !important; }
          .contact-grid { grid-template-columns: 1fr !important; }
          .compare-grid { grid-template-columns: 1fr !important; }
          .audience-grid { grid-template-columns: 1fr 1fr !important; }
          .footer-cols { grid-template-columns: 1fr 1fr !important; }
          .cta-band { flex-direction: column !important; text-align: center !important; }
        }
        @media (max-width: 600px) {
          .hero-heading { font-size: 36px !important; }
          .section-title { font-size: 28px !important; }
          .audience-grid { grid-template-columns: 1fr !important; }
        }

        @media (prefers-reduced-motion: reduce) {
          .h-reveal { opacity: 1 !important; transform: none !important; transition: none !important; }
          .order-card { animation: none !important; opacity: 1 !important; }
          .h-card-hover:hover { transform: none; }
        }
      `}</style>

      {/* ═══ HEADER ═══ */}
      <header style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
        background: scrolled ? "rgba(246,245,241,0.88)" : "transparent",
        backdropFilter: scrolled ? "blur(16px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(16px)" : "none",
        borderBottom: scrolled ? `1px solid ${T.ruled}` : "1px solid transparent",
        padding: scrolled ? "10px 0" : "16px 0",
        transition: "all 0.3s ease",
      }}>
        <Container>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Link to="/" style={{ textDecoration: "none", display: "flex", alignItems: "baseline", gap: "2px" }}>
              <span style={{ fontFamily: T.display, fontSize: "24px", fontWeight: 400, color: T.ink, fontStyle: "italic" }}>Hub</span>
              <span style={{ fontSize: "13px", fontWeight: 700, color: T.leaf, letterSpacing: "-0.5px", fontFamily: T.body }}>360</span>
            </Link>

            <nav className="desktop-nav">
              {["Features", "How it works", "Contact"].map((label) => (
                <button key={label} className="h-nav-link"
                  onClick={() => scrollTo(label.toLowerCase().replace(/ /g, "-"))}>
                  {label}
                </button>
              ))}
              <Button onClick={handleClickOpen} style={{
                background: T.ink, color: T.paperWhite, padding: "7px 20px",
                borderRadius: "6px", fontWeight: 600, textTransform: "none",
                fontSize: "13px", fontFamily: T.body,
              }}>
                Open your store
              </Button>
            </nav>

            <button className="mobile-nav-toggle" onClick={() => setMobileMenuOpen(true)} aria-label="Menu">
              <MenuIcon style={{ fontSize: 24 }} />
            </button>
          </div>
        </Container>
      </header>

      {/* Mobile menu */}
      <div className={`mobile-overlay ${mobileMenuOpen ? "open" : ""}`}>
        <button className="mobile-overlay-close" onClick={() => setMobileMenuOpen(false)} aria-label="Close">
          <CloseIcon style={{ fontSize: 26 }} />
        </button>
        {["Features", "How it works", "Contact"].map((label) => (
          <button key={label} className="mob-item"
            onClick={() => { scrollTo(label.toLowerCase().replace(/ /g, "-")); setMobileMenuOpen(false); }}>
            {label}
          </button>
        ))}
        <Button onClick={() => { handleClickOpen(); setMobileMenuOpen(false); }} style={{
          background: T.ink, color: T.paperWhite, padding: "10px 32px",
          borderRadius: "6px", fontWeight: 600, textTransform: "none",
          fontSize: "15px", fontFamily: T.body, marginTop: 12,
        }}>
          Open your store
        </Button>
      </div>

      {/* ═══ HERO ═══ */}
      <section id="home" style={{ padding: "140px 0 80px" }}>
        <Container>
          <div className="hero-grid" style={{
            display: "grid", gridTemplateColumns: "1fr 380px",
            gap: "48px", alignItems: "start",
          }}>
            <div>
              <p style={{
                fontSize: "13px", fontWeight: 600, color: T.leaf, fontFamily: T.body,
                letterSpacing: "0.5px", margin: "0 0 16px", textTransform: "uppercase",
              }}>
                For wholesale businesses
              </p>

              <h1 className="hero-heading" style={{
                fontFamily: T.display, fontSize: "clamp(38px, 5vw, 56px)",
                fontWeight: 400, lineHeight: 1.1, color: T.ink,
                letterSpacing: "-0.5px", margin: "0 0 24px",
              }}>
                Take orders online,{" "}
                <em style={{ fontStyle: "italic" }}>not over the phone.</em>
              </h1>

              <p style={{
                fontSize: "17px", lineHeight: 1.7, color: T.pencil, fontFamily: T.body,
                margin: "0 0 32px", maxWidth: "480px",
              }}>
                Hub360 gives your wholesale business a simple online storefront
                where customers can browse products, see prices, and place orders
                directly — synced with your Saral Accounting.
              </p>

              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <Button onClick={handleClickOpen} style={{
                  background: T.ink, color: T.paperWhite, padding: "11px 28px",
                  borderRadius: "6px", fontWeight: 600, textTransform: "none",
                  fontSize: "14px", fontFamily: T.body,
                }}>
                  Open your store
                </Button>
                <Button onClick={() => scrollTo("contact")} style={{
                  background: "transparent", color: T.ink, padding: "11px 24px",
                  borderRadius: "6px", fontWeight: 600, textTransform: "none",
                  fontSize: "14px", fontFamily: T.body, border: `1.5px solid ${T.ruled}`,
                }}>
                  Talk to us
                </Button>
              </div>
            </div>

            {/* Right — live order feed */}
            <div style={{ paddingTop: "24px" }}>
              {/* Main live card — cycles through orders */}
              <div key={liveOrderIdx} className="order-live-card" style={{
                background: T.white, border: `1px solid ${T.ruled}`,
                borderRadius: "10px", padding: "20px", maxWidth: "340px", boxShadow: cardShadow,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
                  <div className="pulse-dot" style={{ width: "8px", height: "8px", borderRadius: "50%", background: T.leaf }} />
                  <span style={{ fontSize: "12px", fontWeight: 600, color: T.leaf, fontFamily: T.body }}>New order received</span>
                  <span style={{ fontSize: "11px", color: T.pencilLight, marginLeft: "auto", fontFamily: T.body }}>Just now</span>
                </div>
                <p style={{ fontSize: "15px", fontWeight: 600, color: T.ink, margin: "0 0 4px", fontFamily: T.body }}>
                  {orderFeed[liveOrderIdx].name}
                </p>
                <p style={{ fontSize: "13px", color: T.pencil, margin: "0 0 14px", fontFamily: T.body }}>
                  {orderFeed[liveOrderIdx].items} items &middot; {orderFeed[liveOrderIdx].cats} categories
                </p>
                <div style={{ borderTop: `1px solid ${T.ruled}`, paddingTop: "12px", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: "12px", color: T.pencilLight, fontFamily: T.body }}>Order total</span>
                  <span style={{ fontSize: "18px", fontWeight: 700, color: T.ink, fontFamily: T.body }}>
                    &#8377;{orderFeed[liveOrderIdx].total}
                  </span>
                </div>
              </div>

              {/* Previous orders (faded stack) */}
              {[1, 2].map((offset) => {
                const idx = (liveOrderIdx - offset + orderFeed.length) % orderFeed.length;
                return (
                  <div key={`prev-${idx}`} style={{
                    background: T.white, border: `1px solid ${T.ruled}`,
                    borderRadius: "10px", padding: "12px 20px",
                    maxWidth: `${300 - offset * 20}px`,
                    marginTop: "8px", marginLeft: `${offset * 20}px`,
                    boxShadow: cardShadow, display: "flex", alignItems: "center", gap: "10px",
                    opacity: 1 - offset * 0.25,
                    transition: "all 0.4s ease",
                  }}>
                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: offset === 1 ? T.leaf : T.kraft }} />
                    <div>
                      <p style={{ fontSize: "13px", fontWeight: 600, color: T.ink, margin: 0, fontFamily: T.body }}>
                        {orderFeed[idx].name}
                      </p>
                      <p style={{ fontSize: "12px", color: T.pencilLight, margin: "2px 0 0", fontFamily: T.body }}>
                        {orderFeed[idx].items} items &middot; &#8377;{orderFeed[idx].total}
                      </p>
                    </div>
                  </div>
                );
              })}

              {/* Order count indicator */}
              <div style={{
                marginTop: "16px", display: "flex", alignItems: "center", gap: "6px",
                marginLeft: "4px",
              }}>
                {orderFeed.map((_, i) => (
                  <div key={i} style={{
                    width: i === liveOrderIdx ? "20px" : "6px", height: "6px",
                    borderRadius: "3px",
                    background: i === liveOrderIdx ? T.leaf : T.ruled,
                    transition: "all 0.3s ease",
                  }} />
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      <hr className="h-ruled" style={{ maxWidth: "1200px", margin: "0 auto" }} />

      {/* ═══ FEATURES ═══ */}
      <section id="features" style={{ padding: "80px 0" }}>
        <Container>
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 340px",
            gap: "56px", alignItems: "start",
          }} className="hero-grid">
            {/* Left — feature list */}
            <div>
              <div className="h-reveal" style={{ marginBottom: "48px" }}>
                <h2 className="section-title" style={{
                  fontFamily: T.display, fontSize: "clamp(28px, 4vw, 36px)",
                  fontWeight: 400, color: T.ink, margin: 0, letterSpacing: "-0.3px",
                }}>
                  What Hub360 does for you
                </h2>
              </div>

              <div>
                {[
                  { label: "Online catalog", detail: "Your products with images, categories, and prices — available to your customers 24/7. They browse and order when it suits them." },
                  { label: "Saral Accounting sync", detail: "Products, parties, and prices pull from your Saral data. Orders flow back automatically. No duplicate entry." },
                  { label: "Your brand, your store", detail: "Your business name, your logo, your colors. Customers see your brand on every page — not ours." },
                  { label: "Order management", detail: "See all orders in one place. Filter by date, party, status. Update fulfillment as you go." },
                  { label: "Invoices", detail: "Generate and share proforma invoices the moment an order is confirmed." },
                  { label: "Reports", detail: "Party-wise, category-wise, date-wise — see how your business is doing without digging through spreadsheets." },
                ].map((f, i) => (
                  <div key={i}>
                    <div className="h-reveal h-feature-row" style={{ transitionDelay: `${i * 0.05}s` }}>
                      <h3 style={{ fontSize: "15px", fontWeight: 700, color: T.ink, margin: 0, fontFamily: T.body }}>{f.label}</h3>
                      <p style={{ fontSize: "15px", color: T.pencil, margin: 0, lineHeight: 1.65, fontFamily: T.body }}>{f.detail}</p>
                    </div>
                    <hr className="h-ruled" />
                  </div>
                ))}
              </div>
            </div>

            {/* Right — interactive product catalog demo */}
            <div className="h-reveal" style={{ paddingTop: "100px", transitionDelay: "0.2s" }}>
              <div style={{
                background: T.white, border: `1px solid ${T.ruled}`,
                borderRadius: "10px", overflow: "hidden", boxShadow: cardShadow,
              }}>
                <div style={{
                  padding: "14px 18px", borderBottom: `1px solid ${T.ruled}`,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                  <span style={{ fontSize: "13px", fontWeight: 700, color: T.ink, fontFamily: T.body }}>Product Catalog</span>
                  <span style={{ fontSize: "11px", color: T.pencilLight, fontFamily: T.body }}>
                    Try adding items ↓
                  </span>
                </div>
                {[
                  { id: "CF001", name: "Cotton Roll — White", price: 420, unit: "m" },
                  { id: "PB034", name: "Polyester — Navy", price: 310, unit: "m" },
                  { id: "ST112", name: "Silk Thread — Red", price: 85, unit: "pc" },
                  { id: "DF008", name: "Denim — Indigo", price: 560, unit: "m" },
                ].map((p) => {
                  const qty = demoCart[p.id] || 0;
                  return (
                    <div key={p.id} style={{
                      padding: "10px 18px", borderBottom: `1px solid ${T.ruled}`,
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      background: qty > 0 ? T.leafLight : "transparent",
                      transition: "background 0.2s",
                    }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: "13px", fontWeight: 600, color: T.ink, margin: 0, fontFamily: T.body }}>{p.name}</p>
                        <p style={{ fontSize: "11px", color: T.pencilLight, margin: "2px 0 0", fontFamily: T.body }}>
                          ₹{p.price}/{p.unit}
                          {qty > 0 && <span style={{ color: T.leaf, fontWeight: 600 }}> — ₹{(p.price * qty).toLocaleString()}</span>}
                        </p>
                      </div>
                      {qty === 0 ? (
                        <button className="demo-add-btn"
                          onClick={() => setDemoCart({ ...demoCart, [p.id]: 1 })}>
                          + Add
                        </button>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <button className="demo-qty-btn"
                            onClick={() => setDemoCart({ ...demoCart, [p.id]: Math.max(0, qty - 1) })}>−</button>
                          <span style={{ fontSize: "13px", fontWeight: 700, color: T.ink, fontFamily: T.body, minWidth: "18px", textAlign: "center" }}>{qty}</span>
                          <button className="demo-qty-btn"
                            onClick={() => setDemoCart({ ...demoCart, [p.id]: qty + 1 })}>+</button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Cart summary */}
                {Object.values(demoCart).some((v) => v > 0) && (
                  <div style={{
                    padding: "12px 18px", background: T.ink, display: "flex",
                    justifyContent: "space-between", alignItems: "center",
                    transition: "all 0.3s",
                  }}>
                    <span style={{ fontSize: "12px", color: T.pencilLight, fontFamily: T.body }}>
                      {Object.values(demoCart).reduce((a, b) => a + b, 0)} items in cart
                    </span>
                    <span style={{ fontSize: "14px", fontWeight: 700, color: T.paperWhite, fontFamily: T.body }}>
                      ₹{[
                        { id: "CF001", price: 420 }, { id: "PB034", price: 310 },
                        { id: "ST112", price: 85 }, { id: "DF008", price: 560 },
                      ].reduce((sum, p) => sum + (demoCart[p.id] || 0) * p.price, 0).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
              <p style={{ fontSize: "11px", color: T.pencilLight, margin: "10px 0 0 4px", fontFamily: T.body, fontStyle: "italic" }}>
                This is a demo — try clicking "+ Add"
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="how-it-works" style={{
        padding: "80px 0", background: T.paperWhite,
        borderTop: `1px solid ${T.ruled}`, borderBottom: `1px solid ${T.ruled}`,
      }}>
        <Container>
          <div className="h-reveal" style={{ marginBottom: "48px" }}>
            <h2 className="section-title" style={{
              fontFamily: T.display, fontSize: "clamp(28px, 4vw, 36px)",
              fontWeight: 400, color: T.ink, margin: 0,
            }}>
              Get started in three steps
            </h2>
          </div>

          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: "24px",
          }}>
            {[
              {
                num: "1", title: "Add your products",
                desc: "Upload your catalog — categories, images, prices. Or import directly from Saral Accounting.",
                stat: { value: 5, suffix: " min", label: "avg. import time" },
                visual: (
                  <div style={{
                    background: T.white, border: `1px solid ${T.ruled}`, borderRadius: "8px",
                    padding: "12px 14px", marginTop: "16px",
                  }}>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                      <div style={{ width: "28px", height: "28px", borderRadius: "6px", background: T.leafLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span style={{ fontSize: "12px", color: T.leaf }}>+</span>
                      </div>
                      <span style={{ fontSize: "12px", fontWeight: 600, color: T.ink, fontFamily: T.body }}>Add Product</span>
                    </div>
                    <div style={{ display: "flex", gap: "6px" }}>
                      {["Name", "Price", "Category"].map((f) => (
                        <div key={f} style={{
                          flex: 1, background: T.paper, borderRadius: "4px",
                          padding: "6px 8px", fontSize: "10px", color: T.pencilLight, fontFamily: T.body,
                        }}>{f}</div>
                      ))}
                    </div>
                  </div>
                ),
              },
              {
                num: "2", title: "Connect Saral",
                desc: "Link your Saral Accounting instance. Products and parties sync automatically.",
                stat: { value: 2, suffix: " clicks", label: "to connect" },
                visual: (
                  <div style={{
                    background: T.white, border: `1px solid ${T.ruled}`, borderRadius: "8px",
                    padding: "12px 14px", marginTop: "16px", display: "flex", alignItems: "center", gap: "10px",
                  }}>
                    <div style={{
                      width: "32px", height: "32px", borderRadius: "8px", background: T.leafLight,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "11px", fontWeight: 700, color: T.leaf, fontFamily: T.body,
                    }}>S</div>
                    <div>
                      <p style={{ fontSize: "12px", fontWeight: 600, color: T.ink, margin: 0, fontFamily: T.body }}>Saral Accounting</p>
                      <p style={{ fontSize: "11px", color: T.leaf, margin: "1px 0 0", fontFamily: T.body }}>Connected</p>
                    </div>
                    <div style={{
                      marginLeft: "auto", width: "8px", height: "8px", borderRadius: "50%", background: T.leaf,
                    }} />
                  </div>
                ),
              },
              {
                num: "3", title: "Share with customers",
                desc: "Send your store link to buyers. They place orders, you fulfill — Saral records everything.",
                stat: { value: 1, suffix: " day", label: "to go live" },
                visual: (<StoreLinkDemo />),
              },
            ].map((s, i) => (
              <div key={i} className="h-reveal" style={{
                transitionDelay: `${i * 0.1}s`,
                background: T.paper, border: `1px solid ${T.ruled}`,
                borderRadius: "10px", padding: "28px 24px",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                  <span style={{
                    fontFamily: T.display, fontSize: "32px", color: T.ruled,
                    fontStyle: "italic", lineHeight: 1,
                  }}>
                    {s.num}
                  </span>
                  {s.stat && (
                    <div style={{ textAlign: "right" }}>
                      <span style={{ fontSize: "20px", fontWeight: 700, color: T.ink, fontFamily: T.body }}>
                        <AnimatedCount target={s.stat.value} suffix={s.stat.suffix} />
                      </span>
                      <p style={{ fontSize: "10px", color: T.pencilLight, margin: 0, fontFamily: T.body }}>{s.stat.label}</p>
                    </div>
                  )}
                </div>
                <h3 style={{ fontSize: "16px", fontWeight: 700, color: T.ink, margin: "0 0 8px", fontFamily: T.body }}>{s.title}</h3>
                <p style={{ fontSize: "14px", color: T.pencil, lineHeight: 1.65, margin: 0, fontFamily: T.body }}>{s.desc}</p>
                {s.visual}
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ═══ SARAL INTEGRATION ═══ */}
      <section style={{ padding: "80px 0" }}>
        <Container>
          <div className="h-reveal integration-grid" style={{
            display: "grid", gridTemplateColumns: "1fr 340px",
            gap: "48px", alignItems: "start",
          }}>
            <div style={{
              background: T.white, border: `1px solid ${T.ruled}`,
              borderRadius: "10px", padding: "48px 40px",
            }}>
              <p style={{ fontSize: "12px", fontWeight: 700, color: T.kraft, fontFamily: T.body, letterSpacing: "1px", textTransform: "uppercase", margin: "0 0 12px" }}>Integration</p>
              <h2 className="section-title" style={{
                fontFamily: T.display, fontSize: "clamp(24px, 3.5vw, 32px)",
                fontWeight: 400, color: T.ink, margin: "0 0 16px", lineHeight: 1.2,
              }}>
                Built around Saral Accounting
              </h2>
              <p style={{ fontSize: "15px", color: T.pencil, lineHeight: 1.7, margin: "0 0 24px", fontFamily: T.body }}>
                Hub360 reads your products and parties from Saral, and writes orders back.
                Invoices, stock adjustments, and ledger entries stay current without you touching them.
              </p>
              <div style={{ display: "grid", gap: "10px" }}>
                {[
                  "Products and prices pull from your Saral master",
                  "Orders appear in Saral as they're placed",
                  "Party-wise pricing honored automatically",
                  "Works with your existing Saral setup — no migration needed",
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                    <span style={{ color: T.leaf, fontSize: "14px", fontWeight: 700, lineHeight: "22px", flexShrink: 0 }}>&#10003;</span>
                    <span style={{ fontSize: "14px", color: T.inkSoft, fontWeight: 500, lineHeight: "22px", fontFamily: T.body }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Visual — interactive sync demo */}
            <div style={{ paddingTop: "20px" }}>
              {/* Saral box */}
              <div style={{
                background: T.white, border: `1px solid ${T.ruled}`, borderRadius: "10px",
                padding: "16px 18px", boxShadow: cardShadow,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                  <div style={{
                    width: "28px", height: "28px", borderRadius: "6px", background: T.ink,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "11px", fontWeight: 700, color: T.paperWhite, fontFamily: T.body,
                  }}>S</div>
                  <span style={{ fontSize: "13px", fontWeight: 700, color: T.ink, fontFamily: T.body }}>Saral Accounting</span>
                </div>
                {["Products", "Parties", "Prices"].map((item, i) => (
                  <div key={item} style={{
                    padding: "6px 10px", background: T.paper, borderRadius: "4px",
                    fontSize: "12px", color: T.pencil, marginBottom: "4px", fontFamily: T.body,
                    display: "flex", justifyContent: "space-between",
                    opacity: syncRunning && syncStep <= i ? 0.4 : 1,
                    transition: "opacity 0.3s",
                  }}>
                    <span>{item}</span>
                    <span style={{ color: syncRunning && syncStep > i ? T.leaf : T.pencilLight, fontSize: "11px", fontWeight: 600, transition: "color 0.3s" }}>
                      {syncRunning && syncStep > i ? "✓ sent" : syncRunning && syncStep === i ? "sending..." : "ready"}
                    </span>
                  </div>
                ))}
              </div>

              {/* Animated sync bar */}
              <div style={{ padding: "6px 40px" }}>
                {syncRunning ? (
                  <div className="sync-bar" />
                ) : (
                  <div style={{ height: "3px", background: T.ruled, borderRadius: "2px" }} />
                )}
              </div>

              {/* Hub360 box */}
              <div style={{
                background: T.white, border: `1px solid ${syncRunning && syncStep >= 3 ? T.leafBorder : T.ruled}`, borderRadius: "10px",
                padding: "16px 18px", boxShadow: cardShadow,
                transition: "border-color 0.3s",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                  <div style={{
                    width: "28px", height: "28px", borderRadius: "6px", background: T.leaf,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "11px", fontWeight: 700, color: T.white, fontFamily: T.body,
                  }}>H</div>
                  <span style={{ fontSize: "13px", fontWeight: 700, color: T.ink, fontFamily: T.body }}>Hub360 Store</span>
                  {syncRunning && syncStep >= 6 && (
                    <span style={{ marginLeft: "auto", fontSize: "10px", fontWeight: 700, color: T.leaf, fontFamily: T.body, background: T.leafLight, padding: "2px 8px", borderRadius: "4px" }}>In sync</span>
                  )}
                </div>
                {["Orders", "Invoices", "Stock"].map((item, i) => (
                  <div key={item} style={{
                    padding: "6px 10px", borderRadius: "4px",
                    fontSize: "12px", color: T.inkSoft, marginBottom: "4px", fontFamily: T.body,
                    display: "flex", justifyContent: "space-between",
                    background: syncRunning && syncStep > (i + 3) ? T.leafLight : T.paper,
                    transition: "background 0.3s",
                  }}>
                    <span>{item}</span>
                    <span style={{ color: syncRunning && syncStep > (i + 3) ? T.leaf : T.pencilLight, fontSize: "11px", fontWeight: 600, transition: "color 0.3s" }}>
                      {syncRunning && syncStep > (i + 3) ? "✓ live" : syncRunning && syncStep === (i + 3) ? "syncing..." : "waiting"}
                    </span>
                  </div>
                ))}
              </div>

              {/* Sync button */}
              <button
                onClick={() => {
                  if (syncRunning) return;
                  setSyncRunning(true);
                  setSyncStep(0);
                  let step = 0;
                  const interval = setInterval(() => {
                    step++;
                    setSyncStep(step);
                    if (step >= 7) {
                      clearInterval(interval);
                      setTimeout(() => { setSyncRunning(false); setSyncStep(0); }, 2000);
                    }
                  }, 500);
                }}
                disabled={syncRunning}
                style={{
                  marginTop: "12px", width: "100%", padding: "9px",
                  background: syncRunning ? T.paper : T.ink, color: syncRunning ? T.pencil : T.paperWhite,
                  border: "none", borderRadius: "6px", fontSize: "12px", fontWeight: 700,
                  cursor: syncRunning ? "default" : "pointer", fontFamily: T.body,
                  transition: "all 0.3s",
                }}
              >
                {syncRunning ? (syncStep >= 7 ? "✓ All synced!" : "Syncing data...") : "▶ Run sync demo"}
              </button>
            </div>
          </div>
        </Container>
      </section>

      {/* ═══ BEFORE & AFTER ═══ */}
      <section style={{
        padding: "80px 0", background: T.paperWhite,
        borderTop: `1px solid ${T.ruled}`, borderBottom: `1px solid ${T.ruled}`,
      }}>
        <Container>
          <div className="h-reveal" style={{ marginBottom: "48px" }}>
            <h2 className="section-title" style={{
              fontFamily: T.display, fontSize: "clamp(28px, 4vw, 36px)",
              fontWeight: 400, color: T.ink, margin: 0,
            }}>
              What changes when you go online
            </h2>
          </div>

          <div className="compare-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            <div className="h-reveal" style={{
              background: T.paper, border: `1px solid ${T.ruled}`,
              borderRadius: "10px", padding: "32px 28px",
            }}>
              <p style={{ fontSize: "12px", fontWeight: 700, color: T.pencilLight, fontFamily: T.body, letterSpacing: "0.8px", textTransform: "uppercase", margin: "0 0 20px" }}>Without Hub360</p>
              <div style={{ display: "grid", gap: "14px" }}>
                {[
                  "Customers call or WhatsApp to place orders",
                  "You note down items on paper or in Excel",
                  "Manually enter each order into Saral",
                  "No idea what's in stock until you check",
                  "Pricing disputes because nothing is written",
                  "Evenings spent on data entry instead of business",
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                    <span style={{ fontSize: "13px", color: T.pencilLight, lineHeight: "21px", flexShrink: 0 }}>&times;</span>
                    <span style={{ fontSize: "14px", color: T.pencil, lineHeight: "21px", fontFamily: T.body }}>{item}</span>
                  </div>
                ))}
              </div>

              {/* Visual — messy WhatsApp-style messages */}
              <div style={{ marginTop: "20px", display: "grid", gap: "6px" }}>
                {[
                  { msg: "Bhai 10 pcs white cotton bhejo", time: "9:12 AM" },
                  { msg: "Aur wo blue wala bhi 5 pcs", time: "9:14 AM" },
                  { msg: "Rate kya hai aaj?", time: "9:15 AM" },
                ].map((m, i) => (
                  <div key={i} style={{
                    background: "#dcf8c6", borderRadius: "8px 8px 8px 0",
                    padding: "8px 12px", maxWidth: "220px", position: "relative",
                  }}>
                    <p style={{ fontSize: "12px", color: "#333", margin: 0, fontFamily: T.body }}>{m.msg}</p>
                    <span style={{ fontSize: "9px", color: "#999", float: "right", marginTop: "2px", fontFamily: T.body }}>{m.time}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="h-reveal" style={{
              background: T.white, border: `1px solid ${T.leafBorder}`,
              borderRadius: "10px", padding: "32px 28px", transitionDelay: "0.1s",
            }}>
              <p style={{ fontSize: "12px", fontWeight: 700, color: T.leaf, fontFamily: T.body, letterSpacing: "0.8px", textTransform: "uppercase", margin: "0 0 20px" }}>With Hub360</p>
              <div style={{ display: "grid", gap: "14px" }}>
                {[
                  "Customers browse and order from your catalog anytime",
                  "Orders come in structured, ready to process",
                  "Every order syncs to Saral automatically",
                  "Stock levels visible to you and your customers",
                  "Prices set once, shown clearly on every product",
                  "You focus on fulfillment, not paperwork",
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                    <span style={{ color: T.leaf, fontSize: "13px", fontWeight: 700, lineHeight: "21px", flexShrink: 0 }}>&#10003;</span>
                    <span style={{ fontSize: "14px", color: T.inkSoft, fontWeight: 500, lineHeight: "21px", fontFamily: T.body }}>{item}</span>
                  </div>
                ))}
              </div>

              {/* Visual — clean order card */}
              <div style={{
                marginTop: "20px", background: T.paperWhite, border: `1px solid ${T.ruled}`,
                borderRadius: "8px", padding: "14px 16px",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                  <span style={{ fontSize: "12px", fontWeight: 700, color: T.ink, fontFamily: T.body }}>Order #1042</span>
                  <span style={{
                    fontSize: "10px", fontWeight: 700, color: T.leaf, fontFamily: T.body,
                    background: T.leafLight, padding: "3px 8px", borderRadius: "4px",
                  }}>Confirmed</span>
                </div>
                {[
                  { item: "Cotton Roll — White", qty: "10 m", amt: "₹4,200" },
                  { item: "Polyester — Navy", qty: "5 m", amt: "₹1,550" },
                ].map((row, i) => (
                  <div key={i} style={{
                    display: "flex", justifyContent: "space-between", padding: "5px 0",
                    borderTop: i > 0 ? `1px solid ${T.ruled}` : "none",
                  }}>
                    <span style={{ fontSize: "12px", color: T.pencil, fontFamily: T.body }}>{row.item}</span>
                    <span style={{ fontSize: "12px", color: T.ink, fontWeight: 600, fontFamily: T.body }}>{row.amt}</span>
                  </div>
                ))}
                <div style={{ borderTop: `1px solid ${T.ruled}`, paddingTop: "8px", marginTop: "4px", display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "12px", fontWeight: 700, color: T.ink, fontFamily: T.body }}>Total</span>
                  <span style={{ fontSize: "14px", fontWeight: 700, color: T.ink, fontFamily: T.body }}>₹5,750</span>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ═══ WHO IT'S FOR ═══ */}
      <section style={{ padding: "80px 0" }}>
        <Container>
          <div className="h-reveal" style={{ marginBottom: "48px" }}>
            <h2 className="section-title" style={{
              fontFamily: T.display, fontSize: "clamp(28px, 4vw, 36px)",
              fontWeight: 400, color: T.ink, margin: 0,
            }}>
              Built for businesses like yours
            </h2>
            <p style={{ fontSize: "15px", color: T.pencil, lineHeight: 1.7, margin: "12px 0 0", maxWidth: "520px", fontFamily: T.body }}>
              If you sell to other businesses and use Saral Accounting,
              Hub360 was made with you in mind.
            </p>
          </div>

          <div className="audience-grid" style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px",
          }}>
            {[
              {
                title: "Textile wholesalers",
                desc: "Catalog your fabrics by type, color, and lot. Let retailers browse and order without visiting your godown.",
                icon: "🧵",
              },
              {
                title: "FMCG distributors",
                desc: "Your retailers know what they need. Give them a catalog they can reorder from in two minutes.",
                icon: "📦",
              },
              {
                title: "Hardware suppliers",
                desc: "Thousands of SKUs, dozens of categories. Easier to search online than to call and ask.",
                icon: "🔩",
              },
              {
                title: "Food & grocery wholesalers",
                desc: "Daily orders from regular buyers. Let them place orders at night for morning dispatch.",
                icon: "🛒",
              },
            ].map((item, i) => (
              <div key={i} className="h-reveal h-card-hover" style={{
                background: T.white, border: `1px solid ${T.ruled}`,
                borderRadius: "10px", padding: "24px 22px",
                transitionDelay: `${i * 0.06}s`, cursor: "default",
              }}>
                <span style={{ fontSize: "28px", display: "block", marginBottom: "12px" }}>{item.icon}</span>
                <h3 style={{ fontSize: "15px", fontWeight: 700, color: T.ink, margin: "0 0 8px", fontFamily: T.body }}>{item.title}</h3>
                <p style={{ fontSize: "14px", color: T.pencil, lineHeight: 1.6, margin: 0, fontFamily: T.body }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <hr className="h-ruled" style={{ maxWidth: "1200px", margin: "0 auto" }} />

      {/* ═══ FAQ ═══ */}
      <section style={{ padding: "80px 0" }}>
        <Container>
          <div className="h-reveal" style={{ marginBottom: "48px" }}>
            <h2 className="section-title" style={{
              fontFamily: T.display, fontSize: "clamp(28px, 4vw, 36px)",
              fontWeight: 400, color: T.ink, margin: 0,
            }}>
              Common questions
            </h2>
          </div>

          <div style={{ maxWidth: "680px" }}>
            <hr style={{ border: "none", borderTop: `1px solid ${T.ruled}`, margin: 0 }} />
            {[
              { q: "Do my customers need to install an app?", a: "No. Your store works in any browser — phone, tablet, or computer. Customers just open a link." },
              { q: "What if I don't use Saral Accounting?", a: "Hub360 works on its own too. You can manage products and orders directly. The Saral sync is optional." },
              { q: "Can I set different prices for different customers?", a: "Yes. If you use party-wise pricing in Saral, Hub360 honors it automatically. Each customer sees their own rates." },
              { q: "How long does setup take?", a: "Most businesses are up and running within a day. If you import from Saral, your catalog is ready in minutes." },
              { q: "Is there a free trial?", a: "Yes. You can try Hub360 free before committing. No card required." },
              { q: "Can my customers pay online?", a: "Hub360 focuses on ordering. Payment collection and invoicing happen through your existing process and Saral Accounting." },
            ].map((item, i) => (
              <FaqItem key={i} q={item.q} a={item.a} />
            ))}
          </div>
        </Container>
      </section>

      {/* ═══ CTA BAND ═══ */}
      <section style={{ padding: "64px 0", background: T.ink }}>
        <Container>
          <div className="h-reveal cta-band" style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: "24px",
          }}>
            <div>
              <h2 style={{
                fontFamily: T.display, fontSize: "clamp(24px, 3.5vw, 32px)",
                fontWeight: 400, color: T.paperWhite, margin: "0 0 6px", fontStyle: "italic",
              }}>
                Ready to take your orders online?
              </h2>
              <p style={{ fontSize: "14px", color: T.pencilLight, margin: 0, fontFamily: T.body }}>
                Set up takes minutes. Try it free — no card required.
              </p>
            </div>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <Button onClick={handleClickOpen} style={{
                background: T.paperWhite, color: T.ink, padding: "11px 28px",
                borderRadius: "6px", fontWeight: 600, textTransform: "none",
                fontSize: "14px", fontFamily: T.body,
              }}>
                Open your store
              </Button>
              <Button onClick={() => scrollTo("contact")} style={{
                background: "transparent", color: T.paperWhite, padding: "11px 24px",
                borderRadius: "6px", fontWeight: 600, textTransform: "none",
                fontSize: "14px", fontFamily: T.body, border: "1.5px solid rgba(255,255,255,0.2)",
              }}>
                Talk to us
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* ═══ CONTACT ═══ */}
      <section id="contact" style={{ padding: "80px 0" }}>
        <Container>
          <div className="contact-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "56px" }}>
            <div className="h-reveal">
              <h2 className="section-title" style={{
                fontFamily: T.display, fontSize: "clamp(28px, 4vw, 36px)",
                fontWeight: 400, color: T.ink, margin: "0 0 16px",
              }}>
                Talk to us
              </h2>
              <p style={{ fontSize: "15px", color: T.pencil, lineHeight: 1.7, margin: "0 0 32px", maxWidth: "400px", fontFamily: T.body }}>
                Questions about setup, pricing, or how the Saral integration works?
                We're happy to walk you through it.
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <a href="tel:+919904554232" style={{ display: "flex", alignItems: "center", gap: "12px", textDecoration: "none" }}>
                  <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: T.leafLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <PhoneIcon style={{ fontSize: 16, color: T.leaf }} />
                  </div>
                  <span style={{ fontSize: "14px", fontWeight: 600, color: T.ink, fontFamily: T.body }}>+91 99045 54232</span>
                </a>
                <a href="mailto:info@skyhubs.in" style={{ display: "flex", alignItems: "center", gap: "12px", textDecoration: "none" }}>
                  <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: T.leafLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <MailOutlineIcon style={{ fontSize: 16, color: T.leaf }} />
                  </div>
                  <span style={{ fontSize: "14px", fontWeight: 600, color: T.ink, fontFamily: T.body }}>info@skyhubs.in</span>
                </a>
              </div>
            </div>

            <div className="h-reveal" style={{ transitionDelay: "0.1s" }}>
              <div style={{ background: T.white, padding: "28px", borderRadius: "10px", border: `1px solid ${T.ruled}` }}>
                {contactStatus === "success" && (
                  <div style={{ background: T.leafLight, border: `1px solid ${T.leafBorder}`, borderRadius: "6px", padding: "10px 14px", marginBottom: "16px" }}>
                    <p style={{ fontSize: "13px", color: T.leaf, fontWeight: 600, margin: 0, fontFamily: T.body }}>Message sent. We'll get back to you shortly.</p>
                  </div>
                )}
                {contactStatus === "error" && (
                  <div style={{ background: "rgba(196,69,69,0.06)", border: "1px solid rgba(196,69,69,0.15)", borderRadius: "6px", padding: "10px 14px", marginBottom: "16px" }}>
                    <p style={{ fontSize: "13px", color: T.red, fontWeight: 600, margin: 0, fontFamily: T.body }}>Could not send. Try emailing us directly at info@skyhubs.in.</p>
                  </div>
                )}

                <form onSubmit={handleContactSubmit} style={{ display: "grid", gap: "12px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <input className="h-input" type="text" placeholder="Name" required
                      value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} />
                    <input className="h-input" type="email" placeholder="Email" required
                      value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} />
                  </div>
                  <input className="h-input" type="tel" placeholder="Phone (optional)"
                    value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} />
                  <textarea className="h-input" placeholder="How can we help?" rows="4" required
                    style={{ resize: "vertical" }}
                    value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} />
                  <button type="submit" disabled={contactLoading} style={{
                    width: "100%", padding: "11px", background: T.ink,
                    color: T.paperWhite, border: "none", borderRadius: "6px",
                    fontSize: "14px", fontWeight: 600, fontFamily: T.body,
                    cursor: contactLoading ? "not-allowed" : "pointer",
                    opacity: contactLoading ? 0.6 : 1, transition: "opacity 0.2s",
                  }}>
                    {contactLoading ? <CircularProgress size="1rem" style={{ color: T.paperWhite }} /> : "Send message"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ═══ BACK TO TOP ═══ */}
      <button
        className="back-to-top"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        style={{ opacity: showTopBtn ? 1 : 0, pointerEvents: showTopBtn ? "auto" : "none", transform: showTopBtn ? "translateY(0)" : "translateY(12px)" }}
        aria-label="Back to top"
      >↑</button>

      {/* ═══ FOOTER ═══ */}
      <footer style={{ borderTop: `1px solid ${T.ruled}`, padding: "48px 0 24px", background: T.paperWhite }}>
        <Container>
          <div className="footer-cols" style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: "28px", marginBottom: "40px" }}>
            <div>
              <span style={{ fontFamily: T.display, fontSize: "20px", fontStyle: "italic", color: T.ink }}>Hub</span>
              <span style={{ fontSize: "11px", fontWeight: 700, color: T.leaf, fontFamily: T.body }}>360</span>
              <p style={{ fontSize: "13px", color: T.pencil, lineHeight: 1.6, margin: "10px 0 0", maxWidth: "240px", fontFamily: T.body }}>
                Online ordering for wholesale businesses. Built by Skyhubs Technolabs.
              </p>
            </div>
            {[
              { title: "Product", links: [{ label: "Features", href: "#features" }, { label: "How it works", href: "#how-it-works" }] },
              { title: "Company", links: [{ label: "About", href: "/about" }, { label: "Privacy", href: "/privacy-policy" }, { label: "Terms", href: "/terms" }] },
              { title: "Contact", links: [{ label: "+91 99045 54232", href: "tel:+919904554232" }, { label: "info@skyhubs.in", href: "mailto:info@skyhubs.in" }] },
            ].map((col) => (
              <div key={col.title}>
                <p style={{ fontSize: "12px", fontWeight: 700, color: T.pencilLight, fontFamily: T.body, textTransform: "uppercase", letterSpacing: "0.8px", margin: "0 0 12px" }}>{col.title}</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {col.links.map((l) => (
                    <a key={l.label} href={l.href} style={{ fontSize: "13px", color: T.pencil, textDecoration: "none", fontFamily: T.body }}>{l.label}</a>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <hr className="h-ruled" />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "16px", flexWrap: "wrap", gap: "8px" }}>
            <span style={{ fontSize: "12px", color: T.pencilLight, fontFamily: T.body }}>&copy; {new Date().getFullYear()} Hub360</span>
            <a href="https://skyhubs.in" style={{ fontSize: "12px", color: T.pencilLight, textDecoration: "none", fontFamily: T.body }}>Skyhubs Technolabs</a>
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default MainScreen;

/* ─── Shop Modal ─── */
const ShopModal = ({ handleClose, mobileNo, setMobileNo, mobileNoValid, handleSubmit, isLoading, open }) => (
  <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs"
    PaperProps={{ style: { borderRadius: "10px", background: T.paperWhite, border: `1px solid ${T.ruled}` } }}>
    <DialogTitle style={{ padding: "24px 24px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <p style={{ fontSize: "18px", fontWeight: 700, color: T.ink, margin: 0, fontFamily: T.body }}>Access your store</p>
          <p style={{ fontSize: "13px", color: T.pencil, margin: "4px 0 0", fontFamily: T.body }}>Enter your registered mobile number</p>
        </div>
        <button type="button" onClick={handleClose} style={{
          background: "none", border: `1px solid ${T.ruled}`, width: "28px", height: "28px",
          borderRadius: "6px", fontSize: "16px", cursor: "pointer", color: T.pencil,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>&times;</button>
      </div>
    </DialogTitle>
    <DialogContent style={{ padding: "20px 24px 24px" }}>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: "16px" }}>
          <input className="h-input" type="text" placeholder="10-digit mobile number"
            value={mobileNo} onChange={(e) => setMobileNo(e.target.value)}
            style={{ padding: "12px 14px" }} />
          {!mobileNoValid && (
            <p style={{ color: T.red, fontSize: "12px", marginTop: "6px", fontWeight: 600, fontFamily: T.body }}>
              Please enter a valid 10-digit mobile number
            </p>
          )}
        </div>
        <button type="submit" style={{
          width: "100%", padding: "11px", background: T.ink, color: T.paperWhite,
          border: "none", borderRadius: "6px", fontSize: "14px", fontWeight: 600,
          cursor: "pointer", fontFamily: T.body,
        }}>
          {isLoading ? <CircularProgress size="1.1rem" style={{ color: T.paperWhite }} /> : "Continue"}
        </button>
      </form>
    </DialogContent>
  </Dialog>
);
